import {
  Alert,
  Button,
  Input,
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@fountain/fountain-ui-components';
import { Typography } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import {
  DataCollectionFormField,
  DataCollectionFormResponse,
  WorkflowDataCollectionStage,
  WorkflowEditorService,
} from 'api-clients/monolith';
import produce from 'immer';
import React, { FC, useCallback, useContext, useEffect, useState } from 'react';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from 'react-beautiful-dnd';
import { useIntl } from 'react-intl';
import { v4 as uuid } from 'uuid';

import TextEditor from 'components/TextEditor/Loadable';
import { StageContext } from 'containers/WorkflowEditor/contexts/stageContext';
import { useApiService } from 'hooks/useApiService';
import { useApiServiceMutation } from 'hooks/useApiServiceMutation';
import globalMessages from 'shared/global/messages';

import { messages } from './messages';
import { QuestionCard } from './QuestionCard';
import { useStyles } from './styles';
import {
  EditableQuestion,
  isOptionable,
  KEY_FORMAT,
  QuestionErrors,
} from './types';

export interface EditQuestionsModalProps {
  stage: WorkflowDataCollectionStage;
  onClose: () => void;
}

type FormState = {
  formTitle: string;
  formDescription: string;
  questions: EditableQuestion[];
};

type SaveError = { errors?: Record<string, string[]> };

const toEditable = (field: DataCollectionFormField): EditableQuestion => {
  const { options, restricted, _destroy, ...rest } = field;
  return {
    ...rest,
    uid: String(field.id ?? uuid()),
    options: (options as EditableQuestion['options']) ?? null,
    restricted: restricted ?? null,
    _destroy: Boolean(_destroy),
  };
};

const newQuestion = (): EditableQuestion => ({
  uid: uuid(),
  key: '',
  type: 'text_field',
  question: '',
  predefined: false,
  required: false,
  options: null,
  restricted: null,
});

/**
 * Turns editor rows into the nested-attributes payload the stage accepts. A
 * removed row that was never saved is simply dropped; a saved one is sent with
 * `_destroy`. Order in the array is the position the server assigns.
 */
const toPayload = (state: FormState) => ({
  form_title: state.formTitle,
  form_description: state.formDescription,
  data_fields: state.questions
    .filter(question => !(question._destroy && !question.id))
    .map(question => ({
      id: question.id ?? undefined,
      key: question.key,
      type: question.type,
      question: question.question,
      hint: question.hint ?? undefined,
      predefined: question.predefined,
      required: Boolean(question.required),
      options:
        !question.predefined && isOptionable(question.type)
          ? (question.options ?? [])
          : undefined,
      option_bank_id: question.option_bank_id ?? undefined,
      restricted:
        !question.predefined && question.restricted !== null
          ? question.restricted
          : undefined,
      _destroy: question._destroy ? true : undefined,
    })),
});

const validate = (
  questions: EditableQuestion[],
  intl: ReturnType<typeof useIntl>,
): Record<string, QuestionErrors> => {
  const live = questions.filter(question => !question._destroy);
  const keyCounts: Record<string, number> = {};
  live.forEach(question => {
    if (question.key)
      keyCounts[question.key] = (keyCounts[question.key] ?? 0) + 1;
  });

  const result: Record<string, QuestionErrors> = {};
  live.forEach(question => {
    const errors: QuestionErrors = {};
    if (!question.question.trim()) {
      errors.question = intl.formatMessage(messages.errorQuestionRequired);
    }
    if (!question.key) {
      errors.key = intl.formatMessage(messages.errorKeyRequired);
    } else if (!KEY_FORMAT.test(question.key)) {
      errors.key = intl.formatMessage(messages.errorKeyFormat);
    } else if (keyCounts[question.key] > 1) {
      errors.key = intl.formatMessage(messages.errorKeyDuplicate);
    }
    if (
      !question.predefined &&
      isOptionable(question.type) &&
      !question.option_bank_id &&
      (question.options ?? []).filter(option => option.value.trim()).length ===
        0
    ) {
      errors.options = intl.formatMessage(messages.errorOptionsRequired);
    }
    if (Object.keys(errors).length) result[question.uid] = errors;
  });
  return result;
};

export const EditQuestionsModal: FC<EditQuestionsModalProps> = ({
  stage,
  onClose,
}) => {
  const styles = useStyles();
  const intl = useIntl();
  const { refetchStage } = useContext(StageContext);

  const { result } = useApiService(
    useCallback(
      () =>
        WorkflowEditorService.getInternalApiWorkflowEditorStagesDataCollectionForm(
          stage.external_id,
        ),
      [stage.external_id],
    ),
  );

  const [form, setForm] = useState<FormState | null>(null);
  const [errors, setErrors] = useState<Record<string, QuestionErrors>>({});
  const [serverError, setServerError] = useState<string | null>(null);

  useEffect(() => {
    if (result.status === 'ready' && form === null) {
      const data: DataCollectionFormResponse = result.data;
      setForm({
        formTitle: data.form.form_title ?? '',
        formDescription: data.form.form_description ?? '',
        questions: data.form.data_fields.map(toEditable),
      });
    }
  }, [result, form]);

  const { mutation: save, result: saveResult } = useApiServiceMutation<
    DataCollectionFormResponse,
    typeof WorkflowEditorService.patchInternalApiWorkflowEditorStagesDataCollectionForm,
    SaveError
  >(
    WorkflowEditorService.patchInternalApiWorkflowEditorStagesDataCollectionForm,
    {
      onSuccess: () => {
        refetchStage();
        onClose();
      },
      onError: error => {
        const detail = error?.errors
          ? Object.values(error.errors).flat().join(' ')
          : '';
        setServerError(
          [intl.formatMessage(messages.saveError), detail]
            .filter(Boolean)
            .join(' '),
        );
      },
    },
  );

  const updateQuestion = (uid: string, patch: Partial<EditableQuestion>) =>
    setForm(
      produce(draft => {
        if (!draft) return;
        const target = draft.questions.find(question => question.uid === uid);
        if (target) Object.assign(target, patch);
      }),
    );

  const removeQuestion = (uid: string) =>
    setForm(
      produce(draft => {
        if (!draft) return;
        const index = draft.questions.findIndex(
          question => question.uid === uid,
        );
        if (index === -1) return;
        if (draft.questions[index].id) {
          draft.questions[index]._destroy = true;
        } else {
          draft.questions.splice(index, 1);
        }
      }),
    );

  const addQuestion = () =>
    setForm(
      produce(draft => {
        draft?.questions.push(newQuestion());
      }),
    );

  const onDragEnd = ({ destination, source }: DropResult) => {
    if (!destination || destination.index === source.index) return;
    setForm(
      produce(draft => {
        if (!draft) return;
        const live = draft.questions.filter(question => !question._destroy);
        const [moved] = live.splice(source.index, 1);
        live.splice(destination.index, 0, moved);
        draft.questions = [
          ...live,
          ...draft.questions.filter(question => question._destroy),
        ];
      }),
    );
  };

  const onSave = () => {
    if (!form) return;
    const nextErrors = validate(form.questions, intl);
    setErrors(nextErrors);
    setServerError(null);
    if (Object.keys(nextErrors).length) return;
    save(stage.external_id, toPayload(form));
  };

  const templates =
    result.status === 'ready' ? result.data.standard_questions : [];
  const settings = result.status === 'ready' ? result.data.settings : null;
  const canRestrict = Boolean(
    settings?.sensitive_keys_enabled && settings?.access_secure_data,
  );
  const liveQuestions =
    form?.questions.filter(question => !question._destroy) ?? [];
  const usedKeys = liveQuestions.map(question => question.key).filter(Boolean);
  const blockerCount = Object.keys(errors).length;
  const saving = saveResult.status === 'loading';

  return (
    <Modal
      open
      onClose={onClose}
      ariaLabelledBy="edit-questions-modal-title"
      maxWidth={880}
      fullScreenOnMobile
    >
      <ModalHeader
        onClose={onClose}
        ariaLabelledBy="edit-questions-modal-title"
        showIcon={false}
      >
        {intl.formatMessage(messages.title)}
      </ModalHeader>
      <ModalContent>
        <div className={styles.content}>
          {result.status === 'error' && (
            <Alert type="danger" fullWidth>
              <Typography variant="body2" component="span">
                {intl.formatMessage(messages.loadError)}
              </Typography>
            </Alert>
          )}
          {serverError && (
            <Alert type="danger" fullWidth>
              <Typography variant="body2" component="span">
                {serverError}
              </Typography>
            </Alert>
          )}
          {blockerCount > 0 && (
            <Alert type="danger" fullWidth>
              <Typography variant="body2" component="span">
                {intl.formatMessage(messages.blockerCount, {
                  count: blockerCount,
                })}
              </Typography>
            </Alert>
          )}

          {form === null && result.status !== 'error' && (
            <>
              <Skeleton variant="rect" height={48} />
              <Skeleton variant="rect" className={styles.skeletonCard} />
              <Skeleton variant="rect" className={styles.skeletonCard} />
            </>
          )}

          {form && (
            <>
              <div className={styles.formHeader}>
                <Input
                  fullWidth
                  label={intl.formatMessage(messages.formTitle)}
                  value={form.formTitle}
                  inputProps={{
                    'aria-label': intl.formatMessage(messages.formTitle),
                  }}
                  onChange={event => {
                    const formTitle = event.target.value;
                    setForm(current => current && { ...current, formTitle });
                  }}
                />
                <div>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    className={styles.fieldLabel}
                  >
                    {intl.formatMessage(messages.formDescription)}
                  </Typography>
                  <TextEditor
                    id={`data-collection-form-description-${stage.external_id}`}
                    value={form.formDescription}
                    placeholder={intl.formatMessage(
                      messages.formDescriptionPlaceholder,
                    )}
                    onChange={(formDescription: string) => {
                      setForm(
                        current => current && { ...current, formDescription },
                      );
                    }}
                    disableImageUpload
                    enableLinkButton
                  />
                </div>
              </div>

              <div>
                <Typography variant="h4" className={styles.sectionTitle}>
                  {intl.formatMessage(messages.questions)}
                </Typography>
                {liveQuestions.length === 0 ? (
                  <div className={styles.emptyState}>
                    <Typography variant="h5">
                      {intl.formatMessage(messages.noQuestionsTitle)}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {intl.formatMessage(messages.noQuestionsDescription)}
                    </Typography>
                  </div>
                ) : (
                  <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId="edit-questions-list">
                      {provided => (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          className={styles.questionList}
                        >
                          {liveQuestions.map((question, index) => (
                            <Draggable
                              key={question.uid}
                              draggableId={question.uid}
                              index={index}
                            >
                              {draggableProvided => (
                                <div
                                  ref={draggableProvided.innerRef}
                                  {...draggableProvided.draggableProps}
                                >
                                  <QuestionCard
                                    question={question}
                                    errors={errors[question.uid]}
                                    templates={templates}
                                    usedKeys={usedKeys}
                                    canRestrict={canRestrict}
                                    dragHandleProps={
                                      draggableProvided.dragHandleProps
                                    }
                                    onChange={patch =>
                                      updateQuestion(question.uid, patch)
                                    }
                                    onRemove={() =>
                                      removeQuestion(question.uid)
                                    }
                                  />
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                )}
              </div>

              <div>
                <Button
                  type="secondary"
                  size="small"
                  autoWidth
                  onClick={addQuestion}
                >
                  {intl.formatMessage(messages.addQuestion)}
                </Button>
              </div>
            </>
          )}
        </div>
      </ModalContent>
      <ModalFooter className={styles.footer}>
        <Button type="secondary" size="small" autoWidth onClick={onClose}>
          {intl.formatMessage(globalMessages.cancel)}
        </Button>
        <Button
          type="primary"
          size="small"
          autoWidth
          isLoading={saving}
          disabled={form === null}
          onClick={onSave}
        >
          {intl.formatMessage(messages.saveQuestions)}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
